import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Share2, Check, Copy } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  ShareNote                                                          */
/*                                                                     */
/*  A quiet, dark-Champagne share pill that sits beside an audio       */
/*  player. Behavior:                                                  */
/*    - On mobile browsers that support navigator.share, tapping       */
/*      opens the native OS share sheet (WhatsApp, LinkedIn, X,        */
/*      Instagram DM, Messages, Mail, etc. all appear automatically).  */
/*    - On desktop or unsupported browsers, opens a small popover      */
/*      with WhatsApp, LinkedIn, X and Copy Link.                      */
/*                                                                     */
/*  Instagram cannot be deep-linked from the web (no share URL), so    */
/*  desktop users tap Copy Link and paste into an Instagram DM.        */
/* ------------------------------------------------------------------ */

const socialTargets = ({ url, title, text }) => [
  {
    key: "whatsapp",
    label: "WhatsApp",
    href: `https://api.whatsapp.com/send?text=${encodeURIComponent(
      `${title}\n\n${url}`
    )}`,
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      url
    )}`,
  },
  {
    key: "x",
    label: "X",
    href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      title
    )}&url=${encodeURIComponent(url)}`,
  },
  {
    key: "telegram",
    label: "Telegram",
    href: `https://t.me/share/url?url=${encodeURIComponent(
      url
    )}&text=${encodeURIComponent(title)}`,
  },
];

export default function ShareNote({ title, url, text = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const anchorRef = useRef(null);

  // Close popover on outside click / Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (anchorRef.current && !anchorRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleClick = async () => {
    // Prefer native share sheet on mobile
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title, text: text || title, url });
        return;
      } catch (err) {
        // User cancelled or failed silently, fall through to popover
        if (err && err.name === "AbortError") return;
      }
    }
    setOpen((o) => !o);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      /* ignore */
    }
  };

  const targets = socialTargets({ url, title, text });

  return (
    <div className="relative inline-block" ref={anchorRef}>
      <button
        type="button"
        onClick={handleClick}
        data-testid="share-note-trigger"
        aria-label="Share this note"
        aria-expanded={open}
        className="inline-flex items-center gap-2 rounded-full border border-brass/30 hover:border-brass/70 bg-black/40 backdrop-blur-md px-4 py-2 font-sans text-[11px] uppercase tracking-[0.28em] text-ink-muted hover:text-brass transition-colors duration-500"
      >
        <Share2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        <span>Share</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="share-popover"
            initial={{ opacity: 0, y: 6, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="absolute left-0 mt-3 z-30 min-w-[220px] rounded-2xl border border-brass/25 bg-black/85 backdrop-blur-md p-2 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.7)]"
            data-testid="share-note-popover"
            role="menu"
          >
            <span
              aria-hidden
              className="absolute inset-x-6 top-0 h-px"
              style={{
                background:
                  "linear-gradient(90deg, transparent, rgba(201,169,97,0.5), transparent)",
              }}
            />
            {targets.map((t) => (
              <a
                key={t.key}
                href={t.href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                data-testid={`share-note-${t.key}`}
                role="menuitem"
                className="flex items-center justify-between px-4 py-2.5 rounded-xl font-sans text-[12px] text-ink hover:text-brass hover:bg-brass/5 transition-colors"
              >
                <span>{t.label}</span>
                <span className="font-sans text-[10px] uppercase tracking-[0.28em] text-ink-muted">
                  Open
                </span>
              </a>
            ))}

            <button
              type="button"
              onClick={handleCopy}
              data-testid="share-note-copy"
              role="menuitem"
              className="w-full flex items-center justify-between px-4 py-2.5 mt-1 rounded-xl font-sans text-[12px] text-ink hover:text-brass hover:bg-brass/5 transition-colors border-t border-brass/15"
            >
              <span className="inline-flex items-center gap-2">
                {copied ? (
                  <Check className="h-3.5 w-3.5 text-brass" />
                ) : (
                  <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
                )}
                {copied ? "Link copied" : "Copy link"}
              </span>
              <span className="font-sans text-[10px] uppercase tracking-[0.28em] text-ink-muted">
                {copied ? "Done" : "Paste"}
              </span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
